import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import dayjs from "dayjs";
import { fetchLeaveCoverage } from "../../substitution/services/substitutionService";
import { LeaveCoverage } from "../../substitution/types";
import { LeaveRequest } from "../types";
import { dateRangeLabel, daysLabel } from "../leaveFormat";
import { LEAVE_TEXT_MAX } from "../leaveValidation";

type Props = {
  leave: LeaveRequest | null;
  decision: "approve" | "reject";
  onClose: () => void;
  /** returns null when done, or the reason it failed */
  onConfirm: (note: string) => Promise<string | null>;
};

/** Approve / reject a leave request with an optional note to the faculty. */
export const DecisionDialog: React.FC<Props> = ({ leave, decision, onClose, onConfirm }) => {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coverage, setCoverage] = useState<LeaveCoverage | null>(null);
  const [coverageFailed, setCoverageFailed] = useState(false);

  useEffect(() => {
    setNote("");
    setError(null);
    setCoverage(null);
    setCoverageFailed(false);
    // before approving: can the faculty's periods be covered?
    if (leave && decision === "approve" && leave.periodsAffected > 0) {
      fetchLeaveCoverage(leave.id).then(setCoverage).catch(() => setCoverageFailed(true));
    }
  }, [leave, decision]);

  const approve = decision === "approve";
  const confirm = async () => {
    setSaving(true);
    const failure = await onConfirm(note.trim());
    setSaving(false);
    if (failure) setError(failure);
  };

  return (
    <Dialog open={Boolean(leave)} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs" slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
      <DialogTitle sx={{ fontWeight: 600, pb: 0.5 }}>{approve ? "Approve leave" : "Reject leave"}</DialogTitle>
      <DialogContent>
        {leave && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <b>{leave.applicantName}</b> · {dateRangeLabel(leave)} · {daysLabel(leave.days)}
            {leave.periodsAffected > 0 && ` · ${leave.periodsAffected} periods to cover`}
          </Typography>
        )}
        {approve && leave && leave.periodsAffected > 0 && <CoverageCheck coverage={coverage} failed={coverageFailed} />}
        <TextField
          label={approve ? "Note (optional)" : "Reason for rejecting (optional)"}
          multiline
          minRows={2}
          fullWidth
          size="small"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          disabled={saving}
          helperText={`${note.trim().length} / ${LEAVE_TEXT_MAX}`}
          slotProps={{ htmlInput: { maxLength: LEAVE_TEXT_MAX } }}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={approve ? "success" : "error"}
          onClick={confirm}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
        >
          {approve ? "Approve" : "Reject"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const GAPS_SHOWN = 5;

/** "12 of 14 periods can be covered" + the periods no one is free for. Approving is never blocked. */
const CoverageCheck: React.FC<{ coverage: LeaveCoverage | null; failed: boolean }> = ({ coverage, failed }) => {
  if (failed) return null;
  if (!coverage) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, color: "text.secondary" }}>
        <CircularProgress size={14} />
        <Typography variant="body2">Checking who can cover…</Typography>
      </Stack>
    );
  }
  const { totalPeriods, coverable, gaps } = coverage;
  if (totalPeriods === 0) return null;
  return (
    <Alert severity={gaps.length === 0 ? "success" : "warning"} sx={{ mb: 2, borderRadius: 2 }}>
      <Typography variant="body2" fontWeight={600}>
        {gaps.length === 0
          ? `All ${totalPeriods} periods can be covered.`
          : `${coverable} of ${totalPeriods} periods can be covered. No one is free for:`}
      </Typography>
      {gaps.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {gaps.slice(0, GAPS_SHOWN).map((gap) => (
            <li key={`${gap.date}-${gap.periodName}-${gap.className}`}>
              <Typography variant="body2">
                {dayjs(gap.date).format("ddd D MMM")} {gap.periodName} · {gap.className} · {gap.subjectName}
              </Typography>
            </li>
          ))}
          {gaps.length > GAPS_SHOWN && (
            <Typography component="li" variant="body2" sx={{ listStyle: "none", ml: -2.5, color: "text.secondary" }}>
              and {gaps.length - GAPS_SHOWN} more
            </Typography>
          )}
        </Box>
      )}
      <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
        Assign substitutes in Staff → Substitutions after approving.
      </Typography>
    </Alert>
  );
};
